import React, { useEffect } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Input,
  Textarea,
  Stack,
  Text,
  Flex,
  Image,
} from '@chakra-ui/react';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogCloseTrigger,
} from '../../components/ui/dialog';
import { addCatalogueRequest, updateCatalogueRequest } from '../../store/modelsSlice';
import { Field } from '../../components/ui/field';
import { SelectBox } from '../../components/shared/fields';
import { useColorModeValue } from '../../components/ui/color-mode';
import ButtonBox from '../../components/shared/buttons/ButtonBox';

const CatalogueDialog = ({ isOpen, onClose, mode = 'add', initialData = null }) => {
  const dispatch = useDispatch();
  const { addUpdateLoading } = useSelector((state) => state.models);
  const productCategories = useSelector((state) => state.categories.productCategories || []);
  const productSubcategories = useSelector((state) => state.categories.productSubcategories || {});

  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headerBg = useColorModeValue('blue.50', 'blue.900');
  const headerColor = useColorModeValue('blue.800', 'blue.100');
  const inputBg = useColorModeValue('white', 'gray.700');
  const inputBorder = useColorModeValue('gray.300', 'gray.600');
  const inputFocusBorder = useColorModeValue('blue.500', 'blue.300');
  const sectionBg = useColorModeValue('gray.50', 'gray.750');

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      category: [],
      codeTTLInDays: '',
      description: '',
      images: '',
      model: '',
      qrConfigId: '',
      subCategory: [],
      warrantyPeriodInDays: '',
      extWarrantyPeriodInDays: '',
    },
  });

  useEffect(() => {
    if (isOpen && initialData && mode === 'edit') {
      const categoryValue = initialData.categoryObject?.value ?? undefined;
      const subCategoryValue = initialData.subCategoryObject?.value ?? undefined;

      reset({
        id: initialData.id || '',
        name: initialData.name || '',
        category: categoryValue !== undefined ? [categoryValue] : [],
        codeTTLInDays: initialData.codeTTLInDays || '',
        description: initialData.description || '',
        images: Array.isArray(initialData.images) ? initialData.images.join(', ') : initialData.images || '',
        model: initialData.model || '',
        qrConfigId: initialData.qrConfigId || '',
        subCategory: subCategoryValue !== undefined ? [subCategoryValue] : [],
        warrantyPeriodInDays: initialData.warrantyPeriodInDays || '',
        extWarrantyPeriodInDays: initialData.extWarrantyPeriodInDays || '',
      });
    } else if (isOpen && mode === 'add') {
      reset({
        id: '',
        name: '',
        category: [],
        codeTTLInDays: '',
        description: '',
        images: '',
        model: '',
        qrConfigId: '',
        subCategory: [],
        warrantyPeriodInDays: '',
        extWarrantyPeriodInDays: '',
      });
    }
  }, [isOpen, initialData, mode, reset]);

  const onSubmit = (data) => {
    const processedData = {
      ...data,
      images: data.images ? data.images.split(',').map(img => img.trim()).filter(img => img) : [],
      codeTTLInDays: data.codeTTLInDays ? parseInt(data.codeTTLInDays, 10) : undefined,
      warrantyPeriodInDays: data.warrantyPeriodInDays ? parseInt(data.warrantyPeriodInDays, 10) : undefined,
      extWarrantyPeriodInDays: data.extWarrantyPeriodInDays ? parseInt(data.extWarrantyPeriodInDays, 10) : undefined,
    };

    const normalizeToString = (val) => {
      if (Array.isArray(val)) return val.length ? val[0] : undefined;
      return val;
    };

    if (processedData.category !== undefined) {
      processedData.category = normalizeToString(processedData.category);
    }

    if (processedData.subCategory !== undefined) {
      processedData.subCategory = normalizeToString(processedData.subCategory);
    }

    Object.keys(processedData).forEach(key => {
      if (processedData[key] === '' || processedData[key] === undefined) {
        delete processedData[key];
      }
    });

    if (mode === 'add') {
      dispatch(addCatalogueRequest(processedData));
    } else {
      dispatch(updateCatalogueRequest(processedData));
    }
  };

  const selectedCategory = useWatch({ control, name: 'category' });
  const watchedImages = useWatch({ control, name: 'images' });
  const imageUrls = watchedImages
    ? (typeof watchedImages === 'string'
      ? watchedImages.split(',').map(img => img.trim()).filter(img => img.startsWith('http'))
      : [])
    : [];
  const selectedCategoryValue = Array.isArray(selectedCategory) && selectedCategory.length ? selectedCategory[0] : undefined;
  const subItems = (productSubcategories && selectedCategoryValue) ? (productSubcategories[selectedCategoryValue] || []) : [];

  useEffect(() => {
    if (selectedCategoryValue === undefined) {
      reset({ ...control._formValues, subCategory: [] });
    }
  }, [selectedCategoryValue, reset, control]);

  const [imageModalState, setImageModalState] = React.useState({
    isOpen: false,
    imageUrl: ''
  });

  const handleImageClick = (url, e) => {
    e.stopPropagation();
    setImageModalState({
      isOpen: true,
      imageUrl: url
    });
  };

  return (
    <>
      <DialogRoot open={isOpen} onOpenChange={onClose} size="xl">
        <DialogContent bg={bgColor} borderColor={borderColor}>
          <DialogHeader
            bg={headerBg}
            borderTopRadius="md"
            px={6}
            py={4}
          >
            <DialogTitle
              color={headerColor}
              fontSize="xl"
              fontWeight="bold"
            >
              {mode === 'add' ? '✨ Add New Catalogue' : '✏️ Edit Catalogue'}
            </DialogTitle>
            <DialogCloseTrigger />
          </DialogHeader>

          <DialogBody px={6} py={6}>
            <form onSubmit={handleSubmit(onSubmit)} id="catalogue-form">
              <Stack gap={6}>
                {/* Basic Information Section */}
                <Box
                  bg={sectionBg}
                  p={4}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color={headerColor}
                    mb={4}
                    textTransform="uppercase"
                    letterSpacing="wide"
                  >
                    📋 Basic Information
                  </Text>
                  <Stack gap={4}>

                    <Field
                      label="Name"
                      invalid={!!errors.name}
                      errorText={errors.name?.message}
                      required
                    >
                      <Controller
                        name="name"
                        control={control}
                        rules={{
                          required: 'Name is required',
                          minLength: { value: 3, message: 'Name must be at least 3 characters' }
                        }}
                        render={({ field }) => (
                          <Input
                            {...field}
                            placeholder="Enter product name"
                            bg={inputBg}
                            borderColor={inputBorder}
                            _hover={{ borderColor: inputFocusBorder }}
                            _focus={{ borderColor: inputFocusBorder, boxShadow: `0 0 0 1px ${inputFocusBorder}` }}
                          />
                        )}
                      />
                    </Field>

                    <Field
                      label="Model"
                      invalid={!!errors.model}
                      errorText={errors.model?.message}
                      required
                    >
                      <Controller
                        name="model"
                        control={control}
                        rules={{
                          required: 'Model number is required',
                          minLength: { value: 2, message: 'Model must be at least 2 characters' }
                        }}
                        render={({ field, fieldState }) => (
                          <Input
                            {...field}
                            placeholder="Enter model number"
                            bg={inputBg}
                            borderColor={inputBorder}
                            _hover={{ borderColor: inputFocusBorder }}
                            _focus={{ borderColor: inputFocusBorder, boxShadow: `0 0 0 1px ${inputFocusBorder}` }}
                          />
                        )}
                      />
                    </Field>
                  </Stack>

                  {/* Description Section */}
                  <Field
                    label="Description"
                    invalid={!!errors.description}
                    errorText={errors.description?.message}
                    required
                  >
                    <Controller
                      name="description"
                      control={control}
                      rules={{
                        required: 'Description is required',
                        minLength: { value: 10, message: 'Description must be at least 10 characters' }
                      }}
                      render={({ field }) => (
                        <Textarea
                          {...field}
                          placeholder="Enter detailed product description"
                          rows={3}
                          bg={inputBg}
                          borderColor={inputBorder}
                          _hover={{ borderColor: inputFocusBorder }}
                          _focus={{ borderColor: inputFocusBorder, boxShadow: `0 0 0 1px ${inputFocusBorder}` }}
                        />
                      )}
                    />
                  </Field>

                  {/* Images Section */}
                  <Field
                    label="Images"
                    invalid={!!errors.images}
                    errorText={errors.images?.message}
                    required
                  >
                    <Controller
                      name="images"
                      control={control}
                      rules={{
                        required: 'At least one image URL is required',
                        validate: value => {
                          const list = value ? value.split(',').map(s => s.trim()).filter(s => s) : [];

                          // 1. Must have at least one URL
                          if (list.length === 0) {
                            return 'Provide at least one valid image URL';
                          }

                          // 2. Check duplicates
                          const unique = new Set(list);
                          if (unique.size !== list.length) {
                            return 'Duplicate image URLs are not allowed';
                          }

                          // 3. Define a robust regex for HTTPS image URLs
                          const imageUrlRegex = /^https:\/\/[^\s]+?\.(jpg|jpeg|png|gif|webp|svg|bmp|avif)(\?.*)?$/i;

                          // 4. Validate each URL
                          for (const url of list) {
                            try {
                              // Check valid URL structure
                              new URL(url);
                            } catch {
                              return `Invalid URL format: ${url}`;
                            }

                            // Must start with https
                            if (!url.startsWith('https://')) {
                              return `URL must start with "https://": ${url}`;
                            }

                            // Must be an image file
                            if (!imageUrlRegex.test(url)) {
                              return `Invalid image file type: ${url}`;
                            }

                            // No spaces or newlines
                            if (/\s/.test(url)) {
                              return `URL contains spaces or invalid characters: ${url}`;
                            }
                          }

                          return true;
                        }
                      }}
                      render={({ field }) => (
                        <>
                          <Textarea
                            {...field}
                            placeholder="https://example.com/image1.jpg, https://example.com/image2.png"
                            rows={2}
                            bg={inputBg}
                            borderColor={inputBorder}
                            _hover={{ borderColor: inputFocusBorder }}
                            _focus={{ borderColor: inputFocusBorder, boxShadow: `0 0 0 1px ${inputFocusBorder}` }}
                          />
                          {mode === 'edit' && imageUrls.length > 0 && (
                            <Flex gap={3} flexWrap="wrap" mt={2}>
                              {imageUrls.map((url, index) => (
                                <Box
                                  key={index}
                                  borderWidth="1px"
                                  borderColor={borderColor}
                                  borderRadius="md"
                                  overflow="hidden"
                                  width="120px"
                                  height="120px"
                                  cursor="pointer"
                                  transition="all 0.2s"
                                  _hover={{
                                    transform: 'scale(1.05)',
                                    boxShadow: 'lg',
                                    borderColor: inputFocusBorder
                                  }}
                                  onClick={(e) => handleImageClick(url, e)}
                                >
                                  <Image
                                    src={url}
                                    alt={`Product ${index + 1}`}
                                    width="100%"
                                    height="100%"
                                    objectFit="cover"
                                  />
                                </Box>
                              ))}
                            </Flex>
                          )}
                        </>
                      )}
                    />
                  </Field>
                </Box>

                {/* Categories Section */}
                <Box
                  bg={sectionBg}
                  p={4}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color={headerColor}
                    mb={4}
                    textTransform="uppercase"
                    letterSpacing="wide"
                  >
                    🏷️ Categories
                  </Text>
                  <Flex gap={4}>
                    <Field
                      label="Category"
                      flex="1"
                      invalid={!!errors.category}
                      errorText={errors.category?.message}
                      required
                    >
                      <Controller
                        name="category"
                        control={control}
                        rules={{
                          validate: value => (Array.isArray(value) && value.length > 0) || 'Category is required'
                        }}
                        render={({ field, fieldState }) => (
                          <SelectBox
                            label={null}
                            bg={inputBg}
                            borderColor={inputBorder}
                            name="category"
                            control={control}
                            placeholder="Select Category"
                            items={productCategories}
                            clearable
                            invalid={!!fieldState.error}
                          />
                        )}
                      />
                    </Field>

                    <Field
                      label="Sub Category"
                      flex="1"
                      invalid={!!errors.subCategory}
                      errorText={errors.subCategory?.message}
                      required
                    >
                      <Controller
                        name="subCategory"
                        control={control}
                        rules={{
                          validate: value => (Array.isArray(value) && value.length > 0) || 'Sub Category is required'
                        }}
                        render={({ field, fieldState }) => (
                          <SelectBox
                            label={null}
                            bg={inputBg}
                            borderColor={inputBorder}
                            name="subCategory"
                            control={control}
                            placeholder="Select Sub Category"
                            items={subItems}
                            clearable
                            disabled={!selectedCategoryValue}
                            invalid={!!fieldState.error}
                          />
                        )}
                      />
                    </Field>
                  </Flex>
                </Box>

                {/* Configuration Section */}
                <Box
                  bg={sectionBg}
                  p={4}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    color={headerColor}
                    mb={4}
                    textTransform="uppercase"
                    letterSpacing="wide"
                  >
                    ⚙️ Configuration
                  </Text>
                  <Stack gap={4}>
                    <Flex gap={4}>
                      <Field
                        label="Warranty Period (Days)"
                        flex="1"
                        invalid={!!errors.warrantyPeriodInDays}
                        errorText={errors.warrantyPeriodInDays?.message}
                        required
                      >
                        <Controller
                          name="warrantyPeriodInDays"
                          control={control}
                          rules={{
                            required: 'Warranty period is required',
                            min: { value: 1, message: 'Warranty must be at least 1 day' }
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="number"
                              placeholder="365"
                              bg={inputBg}
                              borderColor={inputBorder}
                              _hover={{ borderColor: inputFocusBorder }}
                              _focus={{ borderColor: inputFocusBorder, boxShadow: `0 0 0 1px ${inputFocusBorder}` }}
                            />
                          )}
                        />
                      </Field>
                      <Field
                        label="Extended Warranty Period (Days)"
                        flex="1"
                        invalid={!!errors.extWarrantyPeriodInDays}
                        errorText={errors.extWarrantyPeriodInDays?.message}
                        required
                      >
                        <Controller
                          name="extWarrantyPeriodInDays"
                          control={control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="number"
                              placeholder="60"
                              bg={inputBg}
                              borderColor={inputBorder}
                              _hover={{ borderColor: inputFocusBorder }}
                              _focus={{ borderColor: inputFocusBorder, boxShadow: `0 0 0 1px ${inputFocusBorder}` }}
                            />
                          )}
                        />
                      </Field>
                    </Flex>
                  </Stack>
                </Box>
              </Stack>
            </form>
          </DialogBody>

          <DialogFooter
            borderTopWidth="1px"
            borderColor={borderColor}
            px={6}
            py={4}
            gap={3}
          >

            <ButtonBox
              name="Cancel"
              variant="outline"
              colorPalette="gray"
              onClick={onClose}
            />

            <ButtonBox
              name={mode === 'add' ? 'Add Catalogue' : 'Update Catalogue'}
              variant="solid"
              colorPalette="blue"
              onClick={handleSubmit(onSubmit)}
              type="submit"
              form="catalogue-form"
              isLoading={addUpdateLoading}
              width={{ base: "100%", md: "auto" }}
            />
          </DialogFooter>
        </DialogContent>
      </DialogRoot>

      {/* Inline Image Preview Dialog */}
      <DialogRoot open={imageModalState.isOpen} onOpenChange={(details) => {
        setImageModalState(details.open ? { ...imageModalState } : { isOpen: false, imageUrl: '' });
      }}>
        <DialogContent
          placement="center"
          portalled
          backdrop
          sx={{
            width: '60vw', height: '60vh', maxWidth: '60vw', maxHeight: '60vh',
            p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column'
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {/* Title could be URL or static */}
              Image Preview
            </DialogTitle>
            <DialogCloseTrigger />
          </DialogHeader>
          <DialogBody sx={{ flex: 1, p: 0, bg: 'gray.50' }}>
            {imageModalState.imageUrl && (
              <img
                src={imageModalState.imageUrl}
                alt="Preview"
                style={{
                  maxWidth: '100%', maxHeight: '100%', objectFit: 'contain',
                  display: 'block', margin: '0 auto'
                }}
              />
            )}
          </DialogBody>
        </DialogContent>
      </DialogRoot>
    </>
  );
};

export default CatalogueDialog;
